import React, { useState, useEffect } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getFatigueStatus, subscribeToFatigueStatus, submitPreTripChecklist } from '../../services/fatigueService';
import toast from 'react-hot-toast';

// ═══════════════ PRE-TRIP CHECKLIST ═══════════════
export default function PreTripChecklist() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const checklistItems = [
    { id: 'adequate_rest', label: 'I have had adequate rest (7+ hours sleep)', critical: true },
    { id: 'no_alcohol', label: 'I am not under the influence of alcohol or drugs', critical: true },
    { id: 'no_medication', label: 'I am not taking medication that impairs driving', critical: true },
    { id: 'physically_fit', label: 'I am physically fit to drive', critical: true },
    { id: 'tyres', label: 'Tyres are in good condition with adequate tread', critical: true },
    { id: 'lights', label: 'All lights are functioning (headlights, brake, indicators)', critical: true },
    { id: 'brakes', label: 'Brakes are responsive', critical: true },
    { id: 'windscreen', label: 'Windscreen is clean and undamaged', critical: false },
    { id: 'interior', label: 'Vehicle interior is clean and presentable', critical: false },
  ];

  const [responses, setResponses] = useState(
    Object.fromEntries(checklistItems.map((item) => [item.id, null]))
  );

  const toggle = (id, value) => setResponses((prev) => ({ ...prev, [id]: value }));
  const allAnswered = Object.values(responses).every((v) => v !== null);
  const criticalFailed = checklistItems.some((item) => item.critical && responses[item.id] === false);

  const handleSubmit = async () => {
    if (!allAnswered) { toast.error('Answer all items'); return; }
    setSubmitting(true);
    try {
      const result = await submitPreTripChecklist(userProfile.id, responses);
      if (result.blocked) {
        toast.error('You cannot drive — a critical item failed. Contact admin.');
      } else {
        toast.success('Checklist completed — you can start driving');
        navigate('/driver');
      }
    } catch (err) { toast.error(err.message); }
    setSubmitting(false);
  };

  return (
    <div className="max-w-[600px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/driver')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Dashboard</button>
      </div>
      <h1 className="font-serif mt-0 mb-2 text-brand-dark">Pre-Trip Checklist</h1>
      <p className="text-body mb-6">Complete before starting any trips today.</p>

      {checklistItems.map((item) => (
        <div key={item.id} className="p-4 rounded-xl mb-2"
          style={{
            border: `1.5px solid ${responses[item.id] === false && item.critical ? '#ff6b6b' : responses[item.id] === true ? '#2dd4a8' : '#e2e8f0'}`,
            background: responses[item.id] === false && item.critical ? 'rgba(255,107,107,0.04)' : '#fff',
          }}>
          <div className="flex justify-between items-center gap-3">
            <div className="flex-1">
              <span className="text-[0.9rem] text-brand-dark">{item.label}</span>
              {item.critical && <span className="text-[0.7rem] ml-1.5" style={{ color: '#ff6b6b' }}>CRITICAL</span>}
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => toggle(item.id, true)} className="px-3.5 py-1.5 rounded-full text-[0.8rem] font-semibold cursor-pointer"
                style={{
                  border: `1.5px solid ${responses[item.id] === true ? '#2dd4a8' : '#e2e8f0'}`,
                  background: responses[item.id] === true ? '#2dd4a8' : '#fff',
                  color: responses[item.id] === true ? '#fff' : '#5a6578',
                }}>Yes</button>
              <button onClick={() => toggle(item.id, false)} className="px-3.5 py-1.5 rounded-full text-[0.8rem] font-semibold cursor-pointer"
                style={{
                  border: `1.5px solid ${responses[item.id] === false ? '#ff6b6b' : '#e2e8f0'}`,
                  background: responses[item.id] === false ? '#ff6b6b' : '#fff',
                  color: responses[item.id] === false ? '#fff' : '#5a6578',
                }}>No</button>
            </div>
          </div>
        </div>
      ))}

      {criticalFailed && (
        <div className="p-3.5 rounded-xl mt-4 font-semibold text-center" style={{ background: 'rgba(255,107,107,0.06)', border: '1px solid rgba(255,107,107,0.2)', color: '#ff6b6b' }}>
          A critical item failed — you will not be able to drive until resolved.
        </div>
      )}

      <button onClick={handleSubmit} disabled={submitting || !allAnswered} className="btn btn-primary w-full mt-5">
        {submitting ? 'Submitting...' : 'Submit Checklist'}
      </button>
      <BackButtonBottom to="/driver" label="Dashboard" />
    </div>
  );
}

// ═══════════════ FATIGUE STATUS ═══════════════
export function FatigueStatus() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [fatigue, setFatigue] = useState(null);

  useEffect(() => {
    if (!userProfile?.id) return;
    const unsub = subscribeToFatigueStatus(userProfile.id, setFatigue);
    return unsub;
  }, [userProfile]);

  if (!fatigue) return <div className="p-10 text-center">Loading...</div>;

  const limits = [
    { label: 'Daily Hours', value: fatigue.dailyHours, max: 10, unit: 'hrs' },
    { label: 'Weekly Hours', value: fatigue.weeklyHours, max: 50, unit: 'hrs' },
    { label: 'Consecutive Days', value: fatigue.consecutiveDays, max: 6, unit: 'days' },
    { label: 'Night Hours (10PM-5AM)', value: fatigue.nightHours, max: 4, unit: 'hrs' },
  ];

  return (
    <div className="max-w-[600px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/driver')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Dashboard</button>
      </div>
      <h1 className="font-serif mt-0 mb-6 text-brand-dark">Fatigue Status</h1>

      {fatigue.lockedOut && (
        <div className="p-5 rounded-2xl text-white mb-6 text-center" style={{ background: '#ff6b6b' }}>
          <div className="text-[1.5rem] mb-2">🛑</div>
          <div className="font-bold text-[1.1rem]">Fatigue Lockout Active</div>
          <div className="text-[0.9rem] mt-1">Reason: {fatigue.lockoutReason || 'Limit reached'}</div>
          <div className="text-[0.85rem] mt-1">You cannot accept bookings until limits reset.</div>
        </div>
      )}

      {limits.map((lim) => {
        const pct = Math.min(100, (lim.value / lim.max) * 100);
        const color = pct >= 90 ? '#ff6b6b' : pct >= 75 ? '#fbbf24' : '#2dd4a8';
        return (
          <div key={lim.label} className="mb-5">
            <div className="flex justify-between mb-1.5">
              <span className="font-semibold text-brand-dark">{lim.label}</span>
              <span className="font-bold" style={{ color }}>{lim.value.toFixed(1)} / {lim.max} {lim.unit}</span>
            </div>
            <div className="h-2.5 rounded-[5px]" style={{ background: '#e2e8f0' }}>
              <div className="h-full rounded-[5px]" style={{ background: color, width: `${pct}%`, transition: 'width 0.5s' }} />
            </div>
          </div>
        );
      })}
      <BackButtonBottom to="/driver" label="Dashboard" />
    </div>
  );
}